// src/client/renderer/vfx/env/crystal-pulse-pool.ts
//
// Plan 18, Task 4.1 — Crystal pulse emissive-intensity modulator.
//
// Behaviour contract:
//   - ALWAYS-ON ambient pool. mode: 'ambient'.
//   - Depends on an optional `getCrystals?(): readonly THREE.Object3D[]` callback
//     supplied by Plan 19's decoration subsystem.
//   - When callback is missing or returns [], pool is a graceful no-op (acquire
//     returns null).
//   - On first acquire(), snapshots the mesh list from the callback and stores each
//     mesh's original emissive intensity in a closure Map. Subsequent acquire()
//     calls are idempotent (return the same cached representative — the first mesh).
//   - update(dt) modulates each crystal's emissiveIntensity via:
//       intensity = baseline * (0.9 + 0.6 * sin(t / period[i] + phase[i]))
//     Produces a purer, longer pulse (period ~1.2-2.5s) vs lantern flicker.
//     Phases and periods are deterministic (derived from mesh index, golden ratio).
//   - isFinished() always returns false.
//   - release() is a no-op — meshes belong to Plan 19's decoration subsystem.
//   - dispose() restores every mesh's original emissiveIntensity, clears the Map,
//     and marks the pool as disposed. CRITICAL: dispose() MUST NOT call
//     geometry.dispose() or material.dispose() — those resources belong to Plan 19.
//   - Colour is NOT modulated — only intensity. Per spec §8 line 598:
//     "crystals (sin pulse glow)".
//
// Limitation (known at write-time):
//   - The mesh list is snapshotted on first acquire(). If Plan 19 adds or removes
//     decoration meshes mid-game, those changes will NOT be reflected in this pool.
//     That is a Plan 19 concern — this pool does not re-query the callback.
//
// Material feature detection:
//   - At acquire() time, each mesh's material is inspected. If 'emissiveIntensity'
//     is present (MeshStandardMaterial / MeshLambertMaterial), that property is
//     modulated. Otherwise, 'opacity' is used as a fallback.

import * as THREE from 'three';
import type { EnvCeremonyPool, EnvCeremonySpawnArgs } from './env-ceremony-types';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Minimum pulse period per crystal (seconds). */
const CRYSTAL_PERIOD_MIN = 1.2;

/** Maximum pulse period per crystal (seconds). */
const CRYSTAL_PERIOD_MAX = 2.5;

/**
 * Deterministic phase for crystal at index i.
 * Uses golden ratio to produce well-distributed phases over [0, 2π).
 */
function crystalPhase(index: number): number {
  return (index * 1.61803) % (Math.PI * 2);
}

/**
 * Deterministic period for crystal at index i.
 * Evenly distributed across 5 slots in [CRYSTAL_PERIOD_MIN, CRYSTAL_PERIOD_MAX].
 */
function crystalPeriod(index: number): number {
  return CRYSTAL_PERIOD_MIN + (index % 5) * ((CRYSTAL_PERIOD_MAX - CRYSTAL_PERIOD_MIN) / 5);
}

// ─────────────────────────────────────────────────────────────────────────────
// Pool config
// ─────────────────────────────────────────────────────────────────────────────

export interface CrystalPulsePoolConfig {
  /** Callback returning current crystal meshes from the decoration subsystem.
   *  Optional — pool is a no-op when absent or returns []. */
  readonly getCrystals?: () => readonly THREE.Object3D[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-mesh state
// ─────────────────────────────────────────────────────────────────────────────

type ModulatedProperty = 'emissiveIntensity' | 'opacity';

interface MeshEntry {
  readonly mesh: THREE.Object3D;
  readonly property: ModulatedProperty;
  readonly originalValue: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility — read/write the modulated property on a material
// ─────────────────────────────────────────────────────────────────────────────

function detectProperty(material: THREE.Material): ModulatedProperty {
  if ('emissiveIntensity' in material) return 'emissiveIntensity';
  return 'opacity';
}

function readProperty(material: THREE.Material, property: ModulatedProperty): number {
  if (property === 'emissiveIntensity') {
    return (material as THREE.MeshStandardMaterial).emissiveIntensity;
  }
  return material.opacity;
}

function writeProperty(material: THREE.Material, property: ModulatedProperty, value: number): void {
  if (property === 'emissiveIntensity') {
    (material as THREE.MeshStandardMaterial).emissiveIntensity = value;
  } else {
    material.opacity = value;
  }
}

function getMaterial(mesh: THREE.Object3D): THREE.Material | null {
  if (mesh instanceof THREE.Mesh) {
    const mat = mesh.material;
    if (Array.isArray(mat)) return mat[0] ?? null;
    return mat;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createCrystalPulsePool(config: CrystalPulsePoolConfig): EnvCeremonyPool {
  const { getCrystals } = config;

  /** Snapshotted mesh entries — null until first acquire(). */
  let entries: MeshEntry[] | null = null;

  /** Cumulative elapsed time (seconds). */
  let elapsed = 0;

  /** Representative mesh returned by acquire() — first mesh in the list. */
  let representative: THREE.Object3D | null = null;

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(_args: EnvCeremonySpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Idempotent re-acquire: return cached representative on subsequent calls.
    if (representative !== null) return representative;

    // No callback → no-op
    if (!getCrystals) return null;

    const meshes = getCrystals();
    if (meshes.length === 0) return null;

    // Snapshot baseline intensities
    entries = [];
    for (const mesh of meshes) {
      const material = getMaterial(mesh);
      if (!material) continue;
      const property = detectProperty(material);
      const originalValue = readProperty(material, property);
      entries.push({ mesh, property, originalValue });
    }

    if (entries.length === 0) return null;

    representative = meshes[0];
    return representative;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (disposed || entries === null || entries.length === 0) return new Set();

    elapsed += deltaSeconds;

    for (let i = 0; i < entries.length; i++) {
      const { mesh, property, originalValue } = entries[i];
      const material = getMaterial(mesh);
      if (!material) continue;

      // Pure sin pulse: intensity = baseline * (0.9 + 0.6 * sin(t/period + phase))
      const phase = crystalPhase(i);
      const period = crystalPeriod(i);
      const sinVal = Math.sin(elapsed / period + phase);
      const newValue = originalValue * (0.9 + 0.6 * sinVal);
      writeProperty(material, property, Math.max(0, newValue));
    }

    const activeSet = new Set<THREE.Object3D>(entries.map((e) => e.mesh));
    return activeSet;
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(_emitter: THREE.Object3D): boolean {
    // Always-on ambient pool — never finished
    return false;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(_emitter: THREE.Object3D): void {
    // No-op: meshes belong to Plan 19's decoration subsystem.
    // Do NOT remove from scene, do NOT dispose geometry or material.
  }

  // ── dispose (F4) ───────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    if (entries !== null) {
      // Restore original intensity on every managed mesh
      for (const { mesh, property, originalValue } of entries) {
        const material = getMaterial(mesh);
        if (!material) continue;
        writeProperty(material, property, originalValue);
      }
      // Clear the internal state
      entries = null;
    }

    representative = null;
    // CRITICAL: do NOT call geometry.dispose() or material.dispose() —
    // those resources belong to Plan 19's decoration subsystem.
  }

  return { mode: 'ambient', acquire, update, isFinished, release, dispose };
}
